import dayjs from 'dayjs';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';

import {
	Box,
	Flex,
	Group,
	LoadingOverlay,
	Pagination,
	ScrollArea,
	Switch,
	Table,
	Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { SecurityItem } from '@/components/security-item/security-item';

import { bookingApi } from '@/shared/api/booking';
import { POLLING_INTERVAL } from '@/shared/constant/api';
import { normalizeCarNumber } from '@/shared/helpers/normalizeCarNumber';
import { chunk } from '@/shared/helpers/utils';
import { BookingsForModerator } from '@/shared/types/booking.types';
import { SearchInput } from '@/shared/uikit/components/input';
import { LayoutRow } from '@/shared/uikit/layouts/LayoutRow';

import { IconCheck, IconX } from '@tabler/icons-react';

const getFilteredBookings = (bookings: BookingsForModerator[] | undefined, searchValue: string) => {
	const search = normalizeCarNumber(searchValue);
	return bookings?.filter(
		(booking) =>
			normalizeCarNumber(booking?.booking_object_name).includes(search) ||
			normalizeCarNumber(booking?.fullname).includes(search) ||
			normalizeCarNumber(booking?.car_number).includes(search) ||
			booking?.guest_numbers?.some((phone) => normalizeCarNumber(phone).includes(search)),
	);
};

function Security() {
	const queryClient = useQueryClient();

	const [activePage, setPage] = useState(1);
	const [data, setData] = useState<BookingsForModerator[] | []>([]);
	const [isToday, setToday] = useState(true);
	const [searchValue, setSearchValue] = useState('');

	const { data: bookings, isLoading: isBookingsLoading } = useQuery({
		queryKey: ['security'],
		queryFn: bookingApi.getBookingsForModerator,
		refetchOnMount: true,
		refetchOnReconnect: true,
		refetchOnWindowFocus: true,
		refetchInterval: POLLING_INTERVAL.SECURITY_BOOKINGS,
	});

	const { mutate: trackCarAccess, isPending: isTrackingCarAccess } = useMutation({
		mutationFn: bookingApi.trackCarAccess,
		onSuccess: () => {
			notifications.show({
				message: `Въезд зафиксирован`,
				color: 'green',
				icon: <IconCheck />,
			});

			queryClient.invalidateQueries({ queryKey: ['security'] });
		},
		onError: () => {
			notifications.show({
				message: `Не удалось зафиксировать въезд`,
				color: 'red',
				icon: <IconX />,
			});
		},
	});

	const someCarPass = useMemo(() => data?.some((el) => el?.car_number), [data]);
	const someGuests = useMemo(() => data?.some((el) => el?.guest_numbers?.length), [data]);

	const handleTodayChange = (evt: ChangeEvent<HTMLInputElement>) => {
		setToday(evt.target.checked);
	};

	const handleSearch = (evt: ChangeEvent<HTMLInputElement>) => {
		setSearchValue(evt.currentTarget?.value ?? '');
	};

	const chunked = useMemo(() => {
		if (!data) return [];

		return chunk(data, 10);
	}, [data]);

	const rows = useMemo(
		() =>
			chunked[activePage - 1]?.map((booking, idx) => (
				<SecurityItem
					key={`${booking.booking_object_id}_${idx}`}
					booking={booking}
					someCarPass={someCarPass}
					someGuests={someGuests}
					trackCarAccess={trackCarAccess}
					isTrackingCarAccess={isTrackingCarAccess}
				/>
			)),
		[chunked],
	);

	useEffect(() => {
		if (bookings) {
			const bookingsByDate = isToday
				? bookings?.filter((booking) => dayjs(booking.start).isToday())
				: bookings;

			if (searchValue) {
				const filteredBookings = getFilteredBookings(bookingsByDate, searchValue);

				setPage(1);
				setData(filteredBookings ?? []);
			} else {
				setData(bookingsByDate);
			}

			return;
		}
	}, [bookings, isToday, searchValue, activePage]);

	return (
		<LayoutRow
			title={{
				title: { children: 'Охрана', size: 'h4', fw: 800 },
			}}
			type="once"
			style={{ height: 'calc(100vh - 116px)' }}
		>
			<Box pos="relative" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
				<LoadingOverlay
					visible={isBookingsLoading}
					zIndex={1000}
					overlayProps={{ radius: 2, blur: 2 }}
				/>
				<Group>
					<SearchInput
						w={530}
						value={searchValue}
						onChange={handleSearch}
						placeholder="Поиск по бронированиям"
						helpText="Введите любое значение из возможных или его часть: номер машины, ФИО сотрудника, название объекта и т.д."
					/>

					<Group gap={16}>
						<Switch size="md" checked={isToday} onChange={handleTodayChange} />
						<Text size="md">Сегодня</Text>
					</Group>
				</Group>
				{chunked.length > 0 ? (
					<>
						<ScrollArea h={'100dvh'} pr={'md'} scrollbarSize={12} offsetScrollbars>
							<Table verticalSpacing="md" stickyHeader>
								<Table.Thead>
									<Table.Tr>
										<Table.Th>
											<Text size="md" c="dimmed">
												Клиент
											</Text>
										</Table.Th>
										<Table.Th>
											<Text size="md" c="dimmed">
												Объект
											</Text>
										</Table.Th>
										<Table.Th ta="center">
											<Text size="md" c="dimmed">
												Номер авто
											</Text>
										</Table.Th>
										{someGuests && (
											<Table.Th ta="center">
												<Text size="md" c="dimmed">
													Телефон
												</Text>
											</Table.Th>
										)}
										<Table.Th ta="center">
											<Text size="md" c="dimmed">
												Начало
											</Text>
										</Table.Th>
										<Table.Th ta="center">
											<Text size="md" c="dimmed">
												Конец
											</Text>
										</Table.Th>
										<Table.Th ta="center">
											<Text size="md" c="dimmed">
												Статус
											</Text>
										</Table.Th>
										<Table.Th ta="center">
											<Text size="md" c="dimmed">
												Действия
											</Text>
										</Table.Th>
									</Table.Tr>
								</Table.Thead>
								<Table.Tbody>{rows}</Table.Tbody>
							</Table>
						</ScrollArea>
						<Pagination total={chunked.length} value={activePage} onChange={setPage} mt="sm" />
					</>
				) : (
					<Flex direction="column" align="center" justify="center" h={'calc(100vh - 300px)'}>
						<Text fz={18} fw={700}>
							Пока ничего нет 😔
						</Text>
					</Flex>
				)}
			</Box>
		</LayoutRow>
	);
}
export default Security;
